import { useEffect, useState } from 'react';
import { KeyRound, Lock } from 'lucide-react';
import { ApiError } from '../lib/api.js';
import { useCurrentUser, useChangePassword, useUpdateProfile } from '../lib/auth.js';
import { usePreferences, type PreferenceKey } from '../lib/preferences.js';
import { toast } from '../lib/toast.js';
import { AppShell } from '../components/AppShell.js';
import { Avatar, Modal, PageHead, Spinner } from '../components/ui.js';
import { AvatarPicker } from '../components/AvatarPicker.js';

const ACCESSIBILITY: { key: PreferenceKey; label: string; hint: string }[] = [
  { key: 'reduceMotion', label: 'Reduce motion', hint: 'Turns off animations and transitions.' },
  { key: 'largeText', label: 'Larger text', hint: 'Increases text and controls by about 15%.' },
  { key: 'strongFocus', label: 'Extra-visible focus', hint: 'Adds a high-contrast focus ring for keyboard use.' },
];

export function ProfilePage() {
  const { data: user, isLoading } = useCurrentUser();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const { prefs, set: setPref } = usePreferences();

  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState('');
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Initialize form when user loads.
  useEffect(() => {
    if (user && !name && !avatar) {
      setName(user.name);
      setAvatar(user.avatar);
    }
  }, [user?.id]);

  async function handleProfileUpdate(event: React.FormEvent) {
    event.preventDefault();
    setProfileError(null);
    setProfileSuccess(false);
    setAvatarError(null);

    try {
      await updateProfile.mutateAsync({ name, avatar });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'INVALID_INPUT') {
        // Avatar validation error from the refine
        setAvatarError(caught.message);
      } else {
        setProfileError(caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.');
      }
    }
  }

  async function handlePasswordChange(event: React.FormEvent) {
    event.preventDefault();
    setPasswordError(null);

    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setShowPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      toast.info('Password changed. Other devices have been signed out.');
    } catch (caught) {
      setPasswordError(caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.');
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Loading…" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <AppShell user={user} crumb="Preferences">
      <form onSubmit={handleProfileUpdate}>
        <PageHead
          eyebrow="Your account"
          title="Preferences"
          lede="Update how you appear to your team and how retro.blue works for you."
          actions={
            <button type="submit" className="btn btn-primary" disabled={updateProfile.isPending}>
              {updateProfile.isPending ? 'Saving…' : 'Save preferences'}
            </button>
          }
        />

        <div className="two">
          {/* ------------------------------------------------ Identity */}
          <section className="card pad stack">
            <h3 className="h3">Identity</h3>

            <div className="flex items-center gap-3">
              <Avatar emoji={avatar} name={name || user.name} size="lg" />
              <span className="min-w-0">
                <strong className="block truncate text-[15px]">{user.name}</strong>
                <span className="block truncate text-[12px] text-muted">{user.email}</span>
              </span>
            </div>

            <AvatarPicker value={avatar} onChange={setAvatar} name="avatar" error={avatarError ?? undefined} />

            <label className="field-l">
              Display name
              <input
                className="input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                maxLength={60}
                autoComplete="name"
                placeholder="Your display name"
              />
            </label>

            <label className="field-l">
              Work email
              <input className="input" readOnly value={user.email} aria-describedby="email-hint" />
            </label>
            <p id="email-hint" className="hint">
              <Lock size={11} aria-hidden /> Ask an administrator to change your email address.
            </p>

            <div>
              <button type="button" className="btn" onClick={() => setShowPassword(true)}>
                <KeyRound size={14} aria-hidden /> Change password
              </button>
            </div>

            {profileError ? (
              <p role="alert" className="err-msg">
                {profileError}
              </p>
            ) : null}
            {profileSuccess ? (
              <p role="status" className="notice-neutral">
                Profile updated successfully.
              </p>
            ) : null}
          </section>

          {/* -------------------------------------------- Accessibility */}
          <section className="card pad">
            <h3 className="h3">Accessibility</h3>
            {ACCESSIBILITY.map((item) => (
              <div key={item.key} className="pref">
                <span>
                  <strong>{item.label}</strong>
                  <p>{item.hint}</p>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={prefs[item.key]}
                  aria-label={item.label}
                  className="switch"
                  onClick={() => setPref(item.key, !prefs[item.key])}
                />
              </div>
            ))}
          </section>
        </div>
      </form>

      <Modal
        open={showPassword}
        onClose={() => setShowPassword(false)}
        title="Change password"
        width="sm"
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setShowPassword(false)}>
              Cancel
            </button>
            <button
              type="submit"
              form="change-password"
              className="btn btn-primary"
              disabled={changePassword.isPending}
            >
              {changePassword.isPending ? 'Changing…' : 'Change password'}
            </button>
          </>
        }
      >
        {/* Same as elsewhere: a real form, so required/minLength checks run
            before the request is sent. */}
        <form id="change-password" className="grid gap-3" onSubmit={handlePasswordChange}>
          <label className="field-l">
            Current password
            <input
              className="input"
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
              autoComplete="current-password"
            />
          </label>
          <label className="field-l">
            New password
            <input
              className="input"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              minLength={10}
              autoComplete="new-password"
              aria-describedby="password-hint"
            />
          </label>
          <p id="password-hint" className="hint">
            At least 10 characters. Length beats complexity — a short phrase works well.
          </p>
          {passwordError ? (
            <p role="alert" className="err-msg">
              {passwordError}
            </p>
          ) : null}
        </form>
      </Modal>
    </AppShell>
  );
}
